import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { notify } from 'app/components';
import { 
  EventAbout, 
  EventOccasion,
  EventAddress, 
  EventImage, 
  EventMap, 
  EventDescription,
  TermsAndConditions,
} from '../../components/step-1';
import { SUB_STEP_ALIAS } from './constants';
import { uploadImage } from '../../api';
import { generateUniqueId, isValidURL } from '../../../../utils/commonUtils';
import { IMAGE_TAGS } from '../../../../constants/imageTags';
import { useParams } from 'react-router-dom';

const Step1 = (props) => {
  const { 
    updateProgress,
    updateStep,
    updateSubStep,
    subStep,
    eventImagesRef,
  } = props;

  const { eventData: formData } = useSelector((state) => state.eventCreate);
  const { activeRequests } = useSelector((state) => state.app);

  const [currentScreen, setCurrentScreen] = useState('eventAbout');
  const [error, setError] = useState({});

  const dispatch = useDispatch();
  const params = useParams();

  const validate = (fields, fieldName = '') => {
    const errors = {};

    fields.forEach(field => {
      if (fieldName) {
        if (!formData?.[fieldName]?.[field]) {
          errors[`${fieldName}${field}Error`] = 'This field is required';
        }
      }
      if (!formData[field] && !fieldName) {
        errors[`${field}Error`] = 'This field is required';
      }

      if (field === 'name' && formData[field].length == 0 && !fieldName) {
        errors.nameError = 'Enter an event name to proceed.';
      }

      if (field === 'name' && formData[field].length > 50 && !fieldName) {
        errors.nameError = 'Should be less than 50 characters to catch viewer’s attention easily.';
      }

      if (field === 'meeting_link' && formData?.[field] && !isValidURL(formData[field])) {
        errors[`${field}Error`] = 'Enter a valid meeting link';
      }

    });
    return errors;
  };

  const calculateStep = async () => {
    if (subStep?.source === 'next') {
      if (subStep?.step == 1) setCurrentScreen('eventAbout');
      else {
        const subSteps = SUB_STEP_ALIAS(formData?.type);
        let errors = {};

        if (currentScreen === 'eventAbout') {
          if (formData?.type === 'offline') {
            errors = validate(['name', 'visibility', 'type', 'geo_location']);
          } else {
            errors = validate(['name', 'visibility', 'type']);
          }
        } else if (currentScreen === 'eventOccasion' ) {
    
          if (formData.type === 'online') {
            errors = validate(['meeting_link']);
          } else {
            errors = validate(['location'], 'event_address');
          }
    
        } else if(currentScreen === 'event_address'){
          errors = validate(['name', 'address_line_1', 'city'], 'event_address');
        } else if (currentScreen === 'eventImage') {
          if (formData?.full_image === null && !eventImagesRef?.current?.full_image) {
            errors = { 'upload': 'no image' };
            notify(
              'error', 'Please upload an image to proceed!', 
              { position: 'bottom-center', duration: 2000 },
            );
          } else {
            if (eventImagesRef.current) {
              Promise.all([
                uploadImage({ id: formData?.full_image, image: eventImagesRef.current.full_image, tag: IMAGE_TAGS.EVENT_IMAGE, fileName: `${params?.eventId || generateUniqueId()}_${IMAGE_TAGS.EVENT_IMAGE}`, loaderId: 'event-image-upload' }),
                uploadImage({ id: formData?.image, image: eventImagesRef.current.image, tag: IMAGE_TAGS.EVENT_CROP_IMAGE, fileName: `${params?.eventId || generateUniqueId()}_${IMAGE_TAGS.EVENT_CROP_IMAGE}`, loaderId: 'event-image-upload' }),
              ]).then((resp) => {
                setFormData({
                  ...formData,
                  full_image: resp[0]?.data?.data?.url,
                  image: resp[1]?.data?.data?.url,
                });
              });
            }
          }
        }
    
        if (Object.keys(errors).length > 0) {
          setError(errors);
          updateSubStep((prev) => ({ step: prev?.step - 1, source: 'error' }));
          return;
        }

        if (subSteps[subStep?.step]) {
          setCurrentScreen(subSteps[subStep?.step]);
          updateProgress((33.3 / subSteps.final) * subStep?.step);
        } else {
          updateStep(2)
        };
      }
    } else if(subStep?.source === 'error') {
      return;
    } else {
      const subSteps = SUB_STEP_ALIAS(formData?.type);

      if (subStep?.step === 'final') {
        updateSubStep({ step: subSteps.final, source: 'prev' });
        updateProgress(33.3);
        return;
      }

      setCurrentScreen(subSteps[subStep?.step]);
      updateProgress((33.3 / subSteps.final) * subStep?.step);
    }
  };

  useEffect(() => {
    calculateStep();
  }, [subStep]);

  const setFormData = (data) => {
    dispatch({ type: 'EVENT_CREATE:EVENT_DATA:SET', data });
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {currentScreen === 'eventAbout' && (
        <EventAbout formData={formData}
          setFormData={setFormData}
          error={error}
          setError={setError}/>
      )}
      {currentScreen === 'eventOccasion' && (
        <EventOccasion
          updateSubStep={updateSubStep}
          formData={formData}
          setFormData={setFormData}
          error={error}
          setError={setError}/>
      )}
      {currentScreen === 'event_address' && (
        <EventAddress formData={formData}
          setFormData={setFormData}
          error={error}
          setError={setError}
        />
      )}
      {currentScreen === 'eventMap' && (
        <EventMap formData={formData}
          setFormData={setFormData}/>
      )}
      {currentScreen === 'eventImage' && (
        <EventImage
          formData={formData}
          setFormData={setFormData}
          isEventImageUploading={activeRequests?.includes('event-image-upload')}
          eventImagesRef={eventImagesRef}
        />
      )}
      {currentScreen === 'eventDescription' && (
        <EventDescription formData={formData}
          setFormData={setFormData}/>
      )}
      {currentScreen === 'eventTermsAndConditions' && (
        <TermsAndConditions formData={formData}
          setFormData={setFormData}/>
      )}
    </div>
  );
};

Step1.propTypes = {
  updateProgress: PropTypes.func,
};

Step1.defaultProps = {
  updateProgress: () => {},
};

export default Step1;
